import { useState, useEffect, useContext } from "react";
import ValetApi from "../api/Api";
import ValetRequestedQueue from "./ValetRequestedQueue.js";
import ValetActivityBox from "./ValetActivityBox";
import UserContext from "../auth/UserContext";
import { Link } from "react-router-dom";
import MobileSearchBar from "../components/MobileSearchBar";
import "./ValetDashBoard.css";

const ValetDashBoard = () => {
    const [userTransactionCount, setUserTransactionCount] = useState([]);
    const { activePodiumId, activeLocationId, userDetail } = useContext(UserContext);
    const [showActivity, setShowActivity] = useState(false);
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const [kpis, setKpis] = useState({
        checkInCount: 0,
        checkOutCount: 0,
        inPossessionCount: 0,
    });
    const [podiumCapacity, setPodiumCapacity] = useState(null);

    // fetch VALET ACTIVITY numbers
    useEffect(() => {
        async function fetchTransactions() {
            try {
                setUserTransactionCount([]);
                const res = await ValetApi.getTodayValetActivityByPodiumId(activePodiumId, userTimeZone);
                if (Array.isArray(res.data.userTransactionCount) && res.data.userTransactionCount.length > 0) {
                    setUserTransactionCount(res.data.userTransactionCount);
                } else {
                    setUserTransactionCount([]);
                }
            } catch (err) {
                setUserTransactionCount([]);
                console.error(err);
            }
        }
        if (activePodiumId) fetchTransactions();
    }, [activePodiumId, userTimeZone]);

    // fetch KPI numbers
    useEffect(() => {
        async function fetchKpis() {
            try {
                if (!activePodiumId) return;
                const res = await ValetApi.getPodiumDailyKpis(activePodiumId, userTimeZone);
                setKpis(res.data.kpis || { checkInCount: 0, checkOutCount: 0, inPossessionCount: 0 });
            } catch (err) {
                setKpis({ checkInCount: 0, checkOutCount: 0, inPossessionCount: 0 });
                console.error(err);
            }
        }
        fetchKpis();
    }, [activePodiumId, userTimeZone]);

    // fetch podium capacity
    useEffect(() => {
        async function fetchPodiumCapacity() {
            try {
                if (!activePodiumId) {
                    setPodiumCapacity(null);
                    return;
                }
                const res = await ValetApi.getPodiumById(activePodiumId);
                const rawCapacity = res?.data?.podium?.parkingCapacity;
                setPodiumCapacity(rawCapacity == null ? null : Number(rawCapacity));
            } catch (err) {
                setPodiumCapacity(null);
                console.error(err);
            }
        }
        fetchPodiumCapacity();
    }, [activePodiumId]);

    const inCount = Number(kpis.inPossessionCount || 0);
    const hasCapacity = Number.isFinite(podiumCapacity) && podiumCapacity >= 0;
    const spacesLeft = hasCapacity ? Math.max(0, podiumCapacity - inCount) : null;
    const percentFull = hasCapacity ? (podiumCapacity === 0 ? (inCount > 0 ? 100 : 0) : Math.min(999, Math.round((inCount / podiumCapacity) * 100))) : null;

    return (
        <div className="valet-dashboard-bg">
            <div className="valet-main">
                <div className="valet-kpi-strip">
                    <div className="valet-kpi-item">
                        In: <strong>{kpis.inPossessionCount}</strong>
                    </div>
                    <div className="valet-kpi-item">
                        Out: <strong>{kpis.checkOutCount}</strong>
                    </div>
                    <div className="valet-kpi-item">
                        Avail: <strong>{spacesLeft == null ? "--" : spacesLeft}</strong>
                        <span className="valet-kpi-sub">/{percentFull == null ? "--" : ` ${percentFull}%`}</span>
                    </div>
                    <div className="valet-kpi-item">
                        Day: <strong>{kpis.checkInCount}</strong>
                    </div>
                </div>

                <MobileSearchBar
                    className="valet-mobile-search-form"
                    podiumId={activePodiumId}
                    locationId={activeLocationId}
                />

                <div className="valet-panels">
                    <div className={`valet-panel valet-panel-activity ${showActivity ? "open" : "closed"}`}>
                        <button
                            className="valet-activity-toggle-btn"
                            onClick={() => setShowActivity((v) => !v)}>
                            {showActivity ? "Hide Valet Activity" : "Show Valet Activity"}
                        </button>

                        {showActivity && (
                            <div className="valet-panel-scroll">
                                <ValetActivityBox userTransactionCount={userTransactionCount} />
                            </div>
                        )}
                    </div>

                    <div className="valet-panel valet-panel-queue">
                        <div className="valet-panel-scroll">
                            <ValetRequestedQueue />
                        </div>
                    </div>
                </div>
            </div>

            <Link
                to="/addVehicle"
                className="add-vehicle-btn-fixed"
                aria-label="Add New Vehicle">
                +
            </Link>
        </div>
    );
};

export default ValetDashBoard;
